import {createSlice} from '@reduxjs/toolkit'
import {fetchMyBranchRequest, fetchInquiriesCount, getTxnErrorsAction} from './ac-opsdash.action'
import {
    type BranchRequestProps,
    type ITransactionErrorProps,
    type IInquiriesCountProps,
    type ITxnErrorTxnsProps,
    type ITxnErrorCountProps
} from './ac-opsdash.types'
import {DEFAULT_TXN_ERRORS_STATE, TE_PARAMETER_ID} from './ac-opsdash.utils'
import {numberDecimalFormatter} from 'utils/helperFunctions'

interface IInitialStateProps {
    isLoading: boolean
    error: Record<string, unknown> | null
    myBranchRequest: BranchRequestProps | Record<string, any>
    txnErrors: ITransactionErrorProps
    inquiriesCountProps: IInquiriesCountProps | Record<string, any>
}

const initialState: IInitialStateProps = {
    isLoading: false,
    error: null,
    myBranchRequest: {},
    inquiriesCountProps: {},
    txnErrors: {
        booking: DEFAULT_TXN_ERRORS_STATE,
        awbCreation: DEFAULT_TXN_ERRORS_STATE,
        availAndPrice: DEFAULT_TXN_ERRORS_STATE
    }
}

const acOpsDashboardSlice = createSlice({
    name: 'AC Operational Dashboard',
    initialState,
    reducers: {
        resetACOpsDashboardState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMyBranchRequest.pending, (state) => {
                state.myBranchRequest.isLoading = true
                state.myBranchRequest.error = null
                state.myBranchRequest.response = null
            })
            .addCase(fetchMyBranchRequest.fulfilled, (state, action: any) => {
                state.myBranchRequest.isLoading = false
                state.myBranchRequest.error = null
                state.myBranchRequest.response = action.payload
            })
            .addCase(fetchMyBranchRequest.rejected, (state, action: any) => {
                state.myBranchRequest.isLoading = false
                state.myBranchRequest.error = action.payload.error
                state.myBranchRequest.response = null
            })
            .addCase(getTxnErrorsAction.pending, (state, action) => {
                const portalFunction = action.meta.arg.portalFunction
                const reduxId = TE_PARAMETER_ID.get(portalFunction)?.reduxId
                if (reduxId) {
                    state.txnErrors[reduxId] = {
                        ...state.txnErrors[reduxId],
                        isLoading: true
                    }
                }
            })
            .addCase(getTxnErrorsAction.fulfilled, (state, action) => {
                const portalFunction = action.meta.arg.portalFunction
                const reduxId = TE_PARAMETER_ID.get(portalFunction)?.reduxId
                const {
                    normalCountPercent = 0,
                    errorCountPercent = 0,
                    errorTransactions = [],
                    transactionErrorCount = []
                } = action.payload

                if (reduxId) {
                    state.txnErrors[reduxId] = {
                        ...DEFAULT_TXN_ERRORS_STATE,
                        ...action.payload,
                        normalCountPercent: numberDecimalFormatter(normalCountPercent, 1),
                        errorCountPercent: numberDecimalFormatter(errorCountPercent, 1),
                        errorTransactions: errorTransactions.map((item: ITxnErrorTxnsProps) => ({
                            ...item,
                            errorPercent: `${numberDecimalFormatter(item.errorPercent, 1)}%`
                        })),
                        isLoading: false
                    }
                }
                transactionErrorCount.forEach(({portalFunction, errorCount}: ITxnErrorCountProps) => {
                    const reduxId = TE_PARAMETER_ID.get(portalFunction)?.reduxId
                    if (reduxId) state.txnErrors[reduxId].errorCount = errorCount
                })
            })
            .addCase(getTxnErrorsAction.rejected, (state, action: any) => {
                const portalFunction = action.meta.arg.portalFunction
                const reduxId = TE_PARAMETER_ID.get(portalFunction)?.reduxId
                if (reduxId) {
                    state.txnErrors[reduxId] = {
                        ...DEFAULT_TXN_ERRORS_STATE,
                        isLoading: false,
                        error: action.payload.error
                    }
                }
            })
            .addCase(fetchInquiriesCount.pending, (state) => {
                state.inquiriesCountProps.isLoading = true
                state.inquiriesCountProps.error = null
                state.inquiriesCountProps.countData = null
            })
            .addCase(fetchInquiriesCount.fulfilled, (state, action: any) => {
                state.inquiriesCountProps.isLoading = false
                state.inquiriesCountProps.error = null
                state.inquiriesCountProps.countData = action.payload.inquiryCount
            })
            .addCase(fetchInquiriesCount.rejected, (state, action: any) => {
                state.inquiriesCountProps.isLoading = false
                state.inquiriesCountProps.error = action.payload.error
                state.inquiriesCountProps.countData = null
            })
    }
})

export const {resetACOpsDashboardState} = acOpsDashboardSlice.actions
export const {reducer: acOpsDashboardReducer} = acOpsDashboardSlice
